import React, { useEffect } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

import SoftBox from "StyleFeatures/SoftBox";
import SoftTypography from "StyleFeatures/SoftTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
// import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import { useSoftUIController, setMiniSidenav } from "context";
import colors from "assets/theme/base/colors";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
 const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, route, href, collapse }) => {
    let returnValue;
   if (type === "collapse") {
      // CASE 1: The item has nested children (like your Organization group)
      if (collapse) {
        returnValue = (
          <div key={key}>
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={pathname.includes(key)} // Parent is active if any child is active
              noCollapse={false} // Force arrow to show
            />
            {/* Render children items */}
            <List sx={{ pl: 4 }}> {/* Indent the children */}
              {collapse.map((child) => (
                <NavLink to={child.route} key={child.key} style={{ textDecoration: 'none' }}>
                  <SidenavCollapse
                    color={color}
                    name={child.name}
                    icon={child.icon} // Optional: small icon for children
                    active={pathname === child.route}
                    noCollapse={true}
                  />
                </NavLink>
              ))}
            </List>
          </div>
        );
      } 
      // CASE 2: Standard single items (like Dashboard)
      else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink to={route} key={key}>
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </NavLink>
        );
      }
    } else if (type === "title") {
      returnValue = (
        <SoftTypography
          key={key}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </SoftTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <SoftBox
          key={key}
          mx={2}
          my={1.5}
          height="1px"
          sx={{ backgroundColor: colors.primary.focus }}
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox component={NavLink} display="flex" alignItems="center">
          {/* <SoftBox component={NavLink} to="/" display="flex" alignItems="center"> */}
          {brand && <SoftBox component="img" src={brand} alt="Spec hr Logo" width="1.5rem" />}
          &nbsp;&nbsp;
          <SoftBox
            width={!brandName && "100%"}
            // sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
          <SoftTypography
  style={{ fontSize: "medium", color: "#206d1a" }}
  component="h6"
  variant="button"
  fontWeight="medium"
>
  {brandName}
</SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Divider  style={{ color: "#206d1a"}}/>
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
